import {
    ActivationComments, CustomCard, CustomLoader, InfluencerCard, MediaCard, Modal, ModalAccess,
} from "@/components";
import { Roles, ActivationStatus } from "@/enums";
import { IActivation, IActivationUser, IUser } from "@/models";
import { RouteNames } from "@/router";
import { Component, Vue } from "vue-property-decorator";

@Component({
    name: "ActivationShow",
    components: {
        CustomCard,
        InfluencerCard,
        ActivationComments,
        MediaCard,
        CustomLoader,
        Modal,
        ModalAccess,
    },
})
export default class ActivationShow extends Vue {
    // Data

    activationId = Number(this.$route.params.id);

    routeNames = RouteNames;

    loading = false;

    loadingModal = false;

    showModal = false;

    public showContactsAccessModal = false;

    modalType = ModalType.Brand;

    // Getter & Setters

    get activation(): IActivation | null {
        return this.$store.getters["app/currentActivation"];
    }

    get influencerFilename(): string {
        return this.activation?.influencersFilename;
    }

    get canUserEdit(): boolean {
        if (this.user != null) {
            // Check User permissions - Creator or Brand/Market Users
            // Administrators and user who created campaign can edit all requests
            if (this.user.roleId === Roles.Administrator || (this.user.userUniqueId === this.activation.userUniqueId && this.user.roleId !== Roles.Member)) {
                return true;
            }
            else  {
                return ((this.activation.statusId === ActivationStatus.SubmittedNotLiveYet && this.user.roleId === Roles.ProviderCampaign)
                    || this.activationAuthorizedUser.some(user => user.userId === this.user.userId && user.accessGranted));
            }
        }
        else {
            return false;
        }
    }

    get canUserDelete(): boolean {
        if (this.user != null) {
            // Check User permissions - Creator or Brand/Market Users
            // Administrators and user who created campaign can edit all requests
            if (this.user.roleId === Roles.Administrator || ((this.user.userUniqueId === this.activation.userUniqueId
                || this.currentUserIsCollaborator) && this.activation.statusId === ActivationStatus.Draft)) {
                return true;
            }
            else  {
                return false;
            }
        }
        else {
            return false;
        }
    }

    get isNotMember(): boolean {
        return this.user != null
            && this.user.roleId !== Roles.Member;
    }

    get canUserGoLive(): boolean {
        return this.user != null && this.isNotMember && this.activation.statusId !== 4 && this.activation.queriesAssociated.length > 0
            && (this.user.roleId === Roles.Administrator || this.user.roleId === Roles.ProviderCampaign);
    }

    get activationAuthorizedUser(): IActivationUser[] {
        return this.$store.getters["app/activationUsers"].filter(a => a.accessGranted);
    }

    get currentUserIsCollaborator(): boolean {
        return this.activationAuthorizedUser.some(user => user.userId === this.user.userId && user.accessGranted);
    }

    get canUserChangeAccess(): boolean {
        return this.user != null && this.isNotMember
            && (this.isUserCreator || this.user.roleId === Roles.Administrator || this.currentUserIsCollaborator);
    }

    get canUserSubmit(): boolean {
        return this.user != null && this.isNotMember && !this.activation.canProviderAccess
            && (this.isUserCreator || this.user.roleId === Roles.Administrator || this.currentUserIsCollaborator);
    }

    get influencersLink(): string {
        const url = window.URL || window.webkitURL;
        const data = this.$store.getters["app/influencers"];
        return  data ? url.createObjectURL(data) : "";
    }

    get isUserCreator(): boolean {
        return this.user != null && this.user.userUniqueId === this.activation.userUniqueId;
    }

    get modalTitle(): string {
        switch (this.modalType) {
            case ModalType.Provider: return "Submit";
            case ModalType.Live: return "Set live";
            case ModalType.Delete: return "Delete campaign";
            default: return "Unknown modal type";
        }
    }

    get modalContent(): string {
        switch (this.modalType) {
            case ModalType.Provider: return "Are you sure to submit the campaign to providers?";
            case ModalType.Live: return "Are you sure to set the campaign live?";
            case ModalType.Delete: return "Are you sure to delete the campaign ?";
            default: return "Unknown modal type";
        }
    }

    get user(): IUser | null {
        return this.$store.getters["account/user"];
    }

    // Lifecycle

    async mounted(): Promise<void> {
        await this.loadActivation();
    }

    openLink(link: string): void {
        link = link.match(/^http[s]?:\/\//) ? link : "https://" + link;
        window.open(link);
    }

    // Methods
    // TODO change
    async onModalConfirm(): Promise<void> {
        this.loadingModal = true;
        try {
            let action = "";
            let successMessage = "";
            switch (this.modalType) {
                case ModalType.Provider: action = "app/updateActivationProviderAccess";
                    successMessage = "Providers can now access your campaign";
                    break;
                case ModalType.Live: action = "app/updateActivationLiveState";
                    successMessage = "Your campaign is now live";
                    break;
                case ModalType.Delete: action = "app/deleteActivation";
                    successMessage = "Your campaign is deleted";
                    break;
            }
            if (action !== "") {
                await this.$store.dispatch(action,
                    this.modalType !== ModalType.Live && this.modalType !== ModalType.Delete?
                        { activationId: this.activation.activationId, canAccess: true }
                        : this.activation.activationId);
                this.showModal = false;
                this.$emit("success", successMessage);
            }
        }
        catch (error) {
            this.$emit("error", error);
        }
        finally {
            this.loadingModal = false;
        }
    }

    openModal(modalType: ModalType): void {
        this.modalType = modalType;
        this.showModal = true;
    }

    closeModal(): void {
        this.showModal = false;
    }

    toggleContactsAccessModal(): void {
        this.showContactsAccessModal = !this.showContactsAccessModal;
    }

    async loadActivation(): Promise<void> {
        this.loading = true;
        try {
            await Promise.all([
                this.$store.dispatch("account/loadUser"),
                this.$store.dispatch("app/loadActivationById", this.activationId),
                this.$store.dispatch("app/loadActivationUsers", this.activationId),
            ]);
            await this.loadInfluencersFile();
        }
        catch (error) {
            this.$emit("error", error);
        }
        finally {
            this.loading = false;
        }
    }

    // TODO refacto => redondant avec get activation
    async loadInfluencersFile(): Promise<void>{
        try {
            this.$store.dispatch("app/resetInfluencers");

            const activation: IActivation = this.$store.getters["app/currentActivation"];
            if(activation.influencerBlobName){
                await this.$store.dispatch("app/loadInfluencers", activation.influencerBlobName);
            }
        }
        catch (error) {
            this.$emit("error", error);
        }
    }

    public handleSuccessMessage(message: string): void {
        this.$emit("success", message);
    }

    public handleErrorMessage(message: string): void {
        this.$emit("error", message);
    }

    // TODO refacto pour mettre en commun avec activation show form
    private getExtension(filename: string): string {
        return filename.substring(filename.lastIndexOf(".") + 1, filename.length ) || "";
    }

}

enum ModalType {
    Brand = 1,
    Market = 2,
    Provider = 3,
    Live = 4,
    Delete = 7,
}
